import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgxChartsModule, LegendPosition } from '@swimlane/ngx-charts';
import { HeaderModule } from 'src/app/header/header.module';
import { pollResultsListRoute } from 'src/app/route-names';
import { DialogService, DialogType } from 'src/app/Services/dialog.service';
import { ChoiceCountForStatement, EventResultsService } from 'src/app/Services/event-results.service';

@Component({
    selector: 'app-poll-results',
    imports: [HeaderModule, NgxChartsModule],
    providers: [],
    templateUrl: './poll-results.component.html',
    styleUrl: './poll-results.component.scss'
})
export class PollResultsComponent implements OnInit {
  
  errorText: string = "";
  errorTitle: string = "";
  eventId: number = 0;
  statementId: string = "";
  numStatements: number = 0;
  currentIndex: number = -1;
  statementResults: ChoiceCountForStatement | undefined;
  total: number = 0;
  data: any[] = [];
  animations = true;
  doughnut = true;
  arcWidth = 0.62;
  labels = true;
  legend = true;
  legendPosition = LegendPosition.Below;
  tooltipDisabled = false;

  ngOnInit() {
    this.loadData();
  }

  constructor (
    private readonly router: Router,
    private readonly resultsService: EventResultsService,
    private dialogService: DialogService
  ) {
    this.eventId = history.state.eventId;
    this.statementId = history.state.contestId;
  }

  loadData(){
    this.statementResults = this.resultsService.getStatementResults(this.eventId, this.statementId);
    if(this.statementResults === undefined) {
      this.errorTitle = $localize`No Poll Results`;
      this.errorText = $localize`We could not retrieve any results for this poll question. We are redirecting you back to the list of poll questions.`;
      this.dialogService.openDialog(DialogType.ALERT, this.errorTitle, this.errorText);
      this.backToContestList();
    }
    this.numStatements = this.resultsService.getEventResults(this.eventId)?.statements.length ?? 0;
    this.currentIndex = this.resultsService.getEventResults(this.eventId)?.statements.findIndex((r) => r.statementId === this.statementId) ?? -1;
    this.computeTotal();
    this.data = this.statementResults?.choices.map((c) => { 
      return {name: c.choiceDescription, value: c.choiceCount}
    }) ?? [];
  }

  computeTotal() {
    this.total = 0;
    if( this.statementResults?.choices) {
      for(let choice of this.statementResults.choices){
        this.total += choice.choiceCount;
      }
    }
  }

  // data has variables startAngle and endAngle showing the angles that the pie slice start aand end at.
  // These angles are measured in radians, so 360 degrees is 2*pi radians.
  // The percentage is therefore the percentage the angle difference is out of 2*pi.
  formatTooltip(data: any): string {
    const percentage = (100*(data.endAngle - data.startAngle) / (2 * Math.PI)).toFixed(1);
    return `${data.data.name}: ${data.data.value} (${percentage}%)`;
  }

  // Custom label formatter to show percentages
  formatLabel(label: string): string {
    let value: number = this.data.find((d) => d.data.name === label)?.value ?? 0;
    let dataTotal = 0;
    this.data.forEach((c) => {
      dataTotal += c.value;
    })
    const percentage = (100*value / dataTotal).toFixed(1);
    return ` ${percentage}% `;
  }

  backToContestList() {
    this.router.navigate(pollResultsListRoute, {state: {eventId: this.eventId}});
  }

  previous() {
    if(this.currentIndex === 0) {
      this.backToContestList();
    } else {
      let prevStatementId = this.resultsService.getEventResults(this.eventId)?.statements[(this.currentIndex) - 1].statementId;
      if(prevStatementId) {
        this.statementId = prevStatementId;
        this.loadData();
      } else {
        this.backToContestList();
      }
    }
  }

  next() {
    if(this.currentIndex === this.numStatements-1) {
      this.backToContestList();
    } else {
      let nextStatementId = this.resultsService.getEventResults(this.eventId)?.statements[(this.currentIndex) + 1].statementId;
      if(nextStatementId) {
        this.statementId = nextStatementId;
        this.loadData();
      } else {
        this.backToContestList();
      }
    }
  }
}